/* eslint-disable react/jsx-no-literals */
import React from 'react'
import clsx from 'clsx'
import ArrowBottomBird from 'shared-svg/ArrowBottomBird.svg'
import styles from './ChartSelect.module.css'

interface ChartSelectProps<T> {
  label: string
  selectedItemName?: string
  items: T[]
  onItemClick: (item: T) => void
  isItemSelected: (item: T) => boolean
  getItemName: (item: T) => string
  filterValue: string
  onFilterChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onResetFilter: () => void
  isOpen: boolean
  toggleOpen: (open: boolean) => void
  placeholder?: string
  emptyMessage?: string
}

const ChartSelect = <T,>({
  label,
  selectedItemName,
  items,
  onItemClick,
  isItemSelected,
  getItemName,
  filterValue,
  onFilterChange,
  onResetFilter,
  isOpen,
  toggleOpen,
  placeholder = 'Filter by name',
  emptyMessage = 'No matching results',
}: ChartSelectProps<T>): React.ReactNode => {
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const filterRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (isOpen) {
      filterRef.current?.focus()
    }
  }, [isOpen])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        toggleOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, toggleOpen])

  const handleToggle = (): void => {
    toggleOpen(!isOpen)
  }

  return (
    <div className={styles.selectContainer}>
      <span className={styles.selectLabel}>{label}</span>

      <div
        ref={dropdownRef}
        className={clsx(styles.dropdown, {
          [styles.dropdownOpen]: isOpen,
        })}
      >
        <div
          onClick={handleToggle}
          role="button"
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              handleToggle()
            }
          }}
          className={styles.currentSelectionContainer}
        >
          <span className={styles.currentSelection}>{selectedItemName}</span>
          <ArrowBottomBird className={styles.arrowIcon} />
        </div>

        <div className={styles.dropdownContent}>
          <div className={styles.filterContainer}>
            <input
              value={filterValue}
              onChange={onFilterChange}
              name="Filter"
              ref={filterRef}
              className={styles.dropdownFilter}
              type="text"
              placeholder={placeholder}
            />
            <button type="button" className={styles.resetFilter} onClick={onResetFilter}>
              &times;
            </button>
          </div>

          <ul className={styles.dropdownOptionsContainer}>
            {items?.map((item, index) => (
              <li className={styles.dropdownOption} key={index}>
                <button
                  className={clsx(styles.dropdownOptionActionButton, {
                    [styles.selectedOption]: isItemSelected(item),
                  })}
                  onClick={() => onItemClick(item)}
                  type="button"
                >
                  {isItemSelected(item) && (
                    <span className={styles.selectedCheckMark}>&#x2713;</span>
                  )}
                  {getItemName(item)}
                </button>
              </li>
            ))}

            {items?.length < 1 && <li className={styles.emptyDropdownOption}>{emptyMessage}</li>}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default ChartSelect
